import React from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import EcommerceFulfillmentEnum from "@models/EcommerceFulfillmentEnum";
import ISupplierSettings from "@models/ISupplierSettings";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import SettingsRow from "../SettingsRow/SettingsRow";
import SettingsDependencyChip from "../SettingsPage/SettingsDependencyChip";
import {
    buildFulfillmentOptionsSetting,
    getAvailableFulfillmentOptions,
    getOfferedFulfillmentOptions,
} from "../../../../utils/ecommerceFulfillment";

type Props = {
    /** The working `settings.screens.eCommerce.fulfillmentOptions`, saved or unsaved. */
    fulfillmentOptions: EcommerceFulfillmentEnum[] | null | undefined;
    /** The working Local Pickup / Local Delivery flags; an option can only be offered while its flag is on. */
    hasLocalPickup: boolean;
    hasLocalDelivery: boolean;
    /** The new setting value; null clears it (the checkout then offers every enabled option, shipping first). */
    onChange: (fulfillmentOptions: EcommerceFulfillmentEnum[] | null) => void;
};

const FulfillmentOptionsGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(8rem, max-content) max-content max-content;
    column-gap: 2rem;
    row-gap: 0.5rem;
    align-items: center;

    .FulfillmentOptions-header {
        font-size: 0.85em;
        opacity: 0.75;
        text-align: center;
    }

    .FulfillmentOptions-control {
        justify-self: center;
    }
`;

/**
 * eCommerce > Checkout: which of Shipping / Local Pickup / Local Delivery customers can choose at checkout, and
 * which one a new cart starts on. Saved as settings.screens.eCommerce.fulfillmentOptions (default first), which
 * the checkout and the API both read.
 */
export const EcommerceFulfillmentOptionsSettings = (props: Props) => {
    const intl = useIntl();
    const label = (key: string) => intl.formatMessage({ id: key, defaultMessage: `_${key}_` });

    const flags = { has_local_pickup: props.hasLocalPickup, has_local_delivery: props.hasLocalDelivery };
    const available = getAvailableFulfillmentOptions(flags);
    const offered = getOfferedFulfillmentOptions({
        ...flags,
        settings: { screens: { eCommerce: { fulfillmentOptions: props.fulfillmentOptions } } } as unknown as ISupplierSettings,
    });
    const defaultChoice = offered[0];

    // Without a local option there is nothing to choose: shipping is the only way to receive an order.
    const hasChoice = available.length > 1;

    const setOffered = (option: EcommerceFulfillmentEnum, isOffered: boolean) => {
        const nextOffered = available.filter((candidate) => (candidate === option ? isOffered : offered.includes(candidate)));

        // At least one option stays offered (its checkbox is disabled, so this only guards other callers).
        if (nextOffered.length === 0) {
            return;
        }

        const nextDefault = nextOffered.includes(defaultChoice) ? defaultChoice : nextOffered[0];

        props.onChange(buildFulfillmentOptionsSetting(available, nextOffered, nextDefault));
    };

    const setDefault = (option: EcommerceFulfillmentEnum) => {
        props.onChange(buildFulfillmentOptionsSetting(available, offered, option));
    };

    const editor = (
        <FulfillmentOptionsGrid data-testid={"fulfillment-options-editor"}>
            <span />
            <span className={"FulfillmentOptions-header"}>{label("fulfillment_option_offered")}</span>
            <span className={"FulfillmentOptions-header"}>{label("default")}</span>

            {available.map((option) => {
                const isOffered = offered.includes(option);

                return (
                    <React.Fragment key={option}>
                        {/* Clicking the option's name toggles its Offered checkbox. */}
                        <OwLabel title={option} for={`fulfillment_offered_${option}`} noBottomMargin={true} />
                        <input
                            type={"checkbox"}
                            id={`fulfillment_offered_${option}`}
                            className={"form-check-input m-0 FulfillmentOptions-control"}
                            name={`fulfillment_offered_${option}`}
                            checked={isOffered}
                            // The last offered option cannot be turned off: checkout needs a way to receive the order.
                            disabled={isOffered && offered.length === 1}
                            onChange={(e) => setOffered(option, e.target.checked)}
                            aria-label={`${label(option)}: ${label("fulfillment_option_offered")}`}
                            data-testid={`fulfillment-offered-${option}`}
                        />
                        <input
                            type={"radio"}
                            className={"form-check-input m-0 FulfillmentOptions-control"}
                            name={"fulfillment_default"}
                            checked={option === defaultChoice}
                            disabled={!isOffered}
                            onChange={() => setDefault(option)}
                            aria-label={`${label(option)}: ${label("default")}`}
                            data-testid={`fulfillment-default-${option}`}
                        />
                    </React.Fragment>
                );
            })}
        </FulfillmentOptionsGrid>
    );

    return (
        <SettingsRow
            title={"ecomm_fulfillment_options"}
            detail={"help_text_ecomm_fulfillment_options"}
            name={"ecomm_fulfillment_options"}
            // Changes land in settings.screens, which is what the page diffs against the saved model.
            editedKey={"settings.screens"}
            disabled={!hasChoice}
            disabledReason={
                hasChoice ? null : (
                    <SettingsDependencyChip
                        reasonKey={"requires"}
                        targetTitleKey={"card_local_delivery_pickup"}
                        targetId={"routes-local"}
                        locked={true}
                    />
                )
            }
            alwaysShowChildren={hasChoice}
        >
            {hasChoice ? editor : null}
        </SettingsRow>
    );
};

export default EcommerceFulfillmentOptionsSettings;
